import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../api';
import './AdminLogin.css';

function AdminLogin({ onLogin }) {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const usernameInputRef = useRef(null);

  // Auto-focus username field on mount
  useEffect(() => {
    usernameInputRef.current?.focus();

    // Check if username was saved (Remember Me)
    const savedUsername = localStorage.getItem('remembered_username');
    if (savedUsername) {
      setCredentials(prev => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Enhanced Validation
    if (!credentials.username.trim() || !credentials.password) {
      setError('Please enter both username and password');
      setLoading(false);
      return;
    }

    if (credentials.username.trim().length < 3) {
      setError('Username must be at least 3 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await login(credentials.username, credentials.password);

      if (response.success) {
        // Handle Remember Me
        if (rememberMe) {
          localStorage.setItem('remembered_username', credentials.username);
        } else {
          localStorage.removeItem('remembered_username');
        }

        // Store user info
        localStorage.setItem('admin_user', JSON.stringify(response.user));

        // Call parent onLogin callback
        if (onLogin) {
          onLogin(response.user);
        }

        // Navigate to dashboard
        navigate('/admin/dashboard');
      }
    } catch (error) {
      const errorMessage = error.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);

      // Clear password field on error for security
      setCredentials(prev => ({ ...prev, password: '' }));
    }

    setLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="admin-login">
      <div className="login-bg-pattern"></div>
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-icon-wrapper">
              <div className="login-icon">🔐</div>
            </div>
            <h1>Welcome Back</h1>
            <p>Sign in to access your dashboard</p>
          </div>

          {error && (
            <div className="error-message">
              <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/>
                <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="floating-input-group">
              <div className="input-wrapper">
                <input
                  ref={usernameInputRef}
                  type="text"
                  id="username"
                  name="username"
                  value={credentials.username}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  autoComplete="username"
                  className="floating-input"
                  placeholder=" "
                />
                <label htmlFor="username" className="floating-label">
                  <svg className="label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeWidth="2"/>
                    <circle cx="12" cy="7" r="4" strokeWidth="2"/>
                  </svg>
                  <span>Username</span>
                </label>
              </div>
            </div>

            <div className="floating-input-group">
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  autoComplete="current-password"
                  className="floating-input"
                  placeholder=" "
                />
                <label htmlFor="password" className="floating-label">
                  <svg className="label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="2"/>
                  </svg>
                  <span>Password</span>
                </label>
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" strokeWidth="2"/>
                      <line x1="1" y1="1" x2="23" y2="23" strokeWidth="2"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="3" strokeWidth="2"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="remember-checkbox">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Remember me</span>
              </label>
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? (
                <>
                  <span>Signing in...</span>
                  <span className="btn-spinner"></span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <svg className="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <line x1="5" y1="12" x2="19" y2="12" strokeWidth="2"/>
                    <polyline points="12 5 19 12 12 19" strokeWidth="2"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <button onClick={handleBackToHome} className="btn-back">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="19" y1="12" x2="5" y2="12" strokeWidth="2"/>
                <polyline points="12 19 5 12 12 5" strokeWidth="2"/>
              </svg>
              <span>Back to SSM Portal</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
